import React from "react";
import { ReportContainer } from "../../constant";
import { PSYCHIATRIC_HISTORY_SECTIONS } from "../../../pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/components/form-components/InitialPsychotherapy/modals/PsychiatricEvolutionModal/PsychiatricEvolutionFormConstant";
import {
  PHYSICAL_ASSESSMENT_EXAM_SECTIONS,
  RISK_LEVEL_OPTIONS,
} from "../../../pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/components/form-components/InitialPsychotherapy/InitialPsychotherapyConstant";

// Single mapper for the 3 Initial Psych Evolution modals:
//  - "psychiatricEvolution" → Past Psychodynamic / Medical / Substance Use /
//     Family Psychiatric / Social history (checkbox cards + per-section note)
//  - "mse" → Mental Status Examination sections (checkbox cards + notes + overall note)
//  - "riskAssessment" → selected risk level (radio) + free-text note
type InitialPsychModalType = "psychiatricEvolution" | "mse" | "riskAssessment";

type SectionDef = {
  key: string;
  label: string;
  // Some sections persist their note under a custom key (e.g. mood → moodAffectNote,
  // perceptions → perceptionNote) instead of `${key}Note`. Honor it when present.
  noteKey?: string;
  items: readonly { name: string; label: string }[];
};

// The checkbox modals persist each section as a comma-separated string of the
// selected option names, e.g. { pastPsychodynamicHistory: "a,b,c" }.
const parseSelected = (value: unknown): string[] => {
  if (typeof value === "string") {
    return value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return Array.isArray(value) ? (value as string[]) : [];
};

// Config for the two checkbox-card modals.
const CHECKBOX_MODAL_CONFIG: Record<
  Exclude<InitialPsychModalType, "riskAssessment">,
  {
    title: string;
    sections: readonly SectionDef[];
    overallNoteKey?: string;
    overallNoteLabel?: string;
  }
> = {
  psychiatricEvolution: {
    title: "Psychiatric Evaluation",
    sections: PSYCHIATRIC_HISTORY_SECTIONS,
  },
  mse: {
    title: "Mental Status Examination (MSE)",
    sections: PHYSICAL_ASSESSMENT_EXAM_SECTIONS,
    overallNoteKey: "overallMseNote",
    overallNoteLabel: "Overall MSE Note",
  },
};

interface InitialPsychModalsMapperProps {
  data: any;
  type: InitialPsychModalType;
}

const InitialPsychModalsMapper: React.FC<InitialPsychModalsMapperProps> = ({
  data,
  type,
}) => {
  // ── Risk Assessment: risk level label + free-text note ──
  if (type === "riskAssessment") {
    const note = (data?.riskLevelNote as string) || "";
    const selectedLabel = RISK_LEVEL_OPTIONS.find(
      (option) => option.value === data?.riskLevel
    )?.label;

    if (!selectedLabel && !note.trim()) return null;

    return (
      <ReportContainer label="Risk Assessment">
        {selectedLabel && (
          <p style={{ margin: "5px 0", paddingLeft: 5, fontSize: "14px" }}>
            <strong>Risk Level : </strong>
            {selectedLabel}
          </p>
        )}
        {note.trim() && (
          <p style={{ margin: "5px 0", paddingLeft: 5, fontSize: "14px" }}>
            <strong>Note : </strong>
            {note}
          </p>
        )}
      </ReportContainer>
    );
  }

  // ── Checkbox-card modals (Psychiatric Evolution / MSE): checked options only ──
  const {
    title,
    sections,
    overallNoteKey,
    overallNoteLabel = "Overall Note",
  } = CHECKBOX_MODAL_CONFIG[type];

  const rendered = sections
    .map((section) => {
      const selected = parseSelected(data?.[section.key]);
      const labels = section.items
        .filter((it) => selected.includes(it.name))
        .map((it) => it.label);
      const note =
        (data?.[section.noteKey ?? `${section.key}Note`] as string) || "";
      return { key: section.key, label: section.label, labels, note };
    })
    .filter((s) => s.labels.length > 0 || s.note.trim());

  const overallNote = overallNoteKey
    ? (data?.[overallNoteKey] as string) || ""
    : "";

  if (rendered.length === 0 && !overallNote.trim()) return null;

  return (
    <ReportContainer label={title}>
      {rendered.map((s) => (
        <div key={s.key} style={{ marginBottom: 8, paddingLeft: 5 }}>
          <p style={{ margin: "4px 0", fontSize: "14px" }}>
            <strong>{s.label}</strong>
          </p>
          {s.labels.length > 0 && (
            <p style={{ margin: "2px 0", fontSize: "14px" }}>
              {s.labels.join(", ")}
            </p>
          )}
          {s.note.trim() && (
            <p style={{ margin: "2px 0", fontSize: "14px" }}>
              <strong>Note : </strong>
              {s.note}
            </p>
          )}
        </div>
      ))}
      {overallNote.trim() && (
        <p style={{ margin: "6px 0", paddingLeft: 5, fontSize: "14px" }}>
          <strong>{overallNoteLabel} : </strong>
          {overallNote}
        </p>
      )}
    </ReportContainer>
  );
};

export default InitialPsychModalsMapper;
